// src/pages/StarRec/businesses/Manage.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../../../api/apiConfig";
import {
  getApprovedBusinesses,
  getBlacklistedBusinesses,
  blacklistBusiness,
  removeFromBlacklist,
} from "../../../api/business/manageApi";

/* -------------------- MESSAGE BAR -------------------- */
function MessageBar({ message, type = "info", onClose }) {
  if (!message) return null;
  const alertClass =
    type === "error" ? "alert-danger" : type === "success" ? "alert-success" : "alert-info";

  return (
    <div className={`alert ${alertClass} d-flex justify-content-between align-items-center`}>
      <div>{message}</div>
      <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

/* -------------------- MODAL -------------------- */
function Modal({ open, title, titleClassName = "", children, onClose }) {
  if (!open) return null;
  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white border rounded p-3"
        style={{ width: "min(900px, 100%)", maxHeight: "90vh", overflow: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className={`m-0 ${titleClassName}`}>{title}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ViewBusinessModal({ open, business, onClose, onBlacklist, onRemoveBlacklist, processing }) {
  if (!open || !business) return null;

  const isBlacklisted = business.blacklisted || business.isBlacklisted;

  const Field = ({ label, value }) => (
    <div className="mb-3">
      <label className={`form-label fw-semibold ${isBlacklisted ? "text-danger" : ""}`}>{label}</label>
      <input className="form-control" value={value ?? ""} readOnly />
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isBlacklisted ? "View Blacklisted Business Info" : "View Business Info"}
      titleClassName={isBlacklisted ? "text-danger" : ""}
    >
      <div className="mb-3">
        <small className="text-muted">
          Business ID: <strong>{business._id || business.id}</strong>
        </small>
        {isBlacklisted ? (
          <div className="alert alert-danger mt-2 py-2">
            <small>This business is currently blacklisted</small>
          </div>
        ) : null}
      </div>

      <div className="row">
        <div className="col-md-6">
          <Field label="Business Name" value={business.businessName} />
        </div>
        <div className="col-md-6">
          <Field label="Branch Name" value={business.branchName} />
        </div>

        <div className="col-md-4">
          <Field label="Country" value={business.country} />
        </div>
        <div className="col-md-4">
          <Field label="State" value={business.state} />
        </div>
        <div className="col-md-4">
          <Field label="City" value={business.city} />
        </div>

        <div className="col-md-6">
          <Field label="Business Entity" value={business.businessEntity} />
        </div>
        <div className="col-md-6">
          <Field label="Registration Number" value={business.registrationNumber} />
        </div>

        <div className="col-md-6">
          <Field label="Business Category" value={business.businessCategory} />
        </div>
        <div className="col-md-6">
          <Field label="Total Employees" value={String(business.totalEmployees ?? "")} />
        </div>

        <div className="col-md-6">
          <Field label="Contact Person Name" value={business.contactPersonName} />
        </div>
        <div className="col-md-6">
          <Field label="Email" value={business.email} />
        </div>

        <div className="col-md-6">
          <Field label="Mobile" value={business.mobileNumber} />
        </div>

        {business.blacklistReason ? (
          <div className="col-12">
            <Field label="Blacklist Reason" value={business.blacklistReason} />
          </div>
        ) : null}
      </div>

      <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
        <button className="btn btn-outline-secondary" onClick={onClose} disabled={processing}>
          Back
        </button>

        {!isBlacklisted ? (
          <button
            className="btn btn-outline-danger"
            onClick={() => onBlacklist(business._id || business.id)}
            disabled={processing}
          >
            {processing ? "Processing..." : "Blacklist"}
          </button>
        ) : (
          <button
            className="btn btn-outline-success"
            onClick={() => onRemoveBlacklist(business._id || business.id)}
            disabled={processing}
          >
            {processing ? "Processing..." : "Remove Blacklist"}
          </button>
        )}
      </div>
    </Modal>
  );
}

function BlacklistedListModal({ open, rows, onClose, onView, loading }) {
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="Blacklisted Businesses" titleClassName="text-danger">
      {loading ? (
        <div className="text-center my-4">
          <div className="spinner-border" role="status" />
          <p className="mt-2">Loading blacklisted businesses...</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-bordered table-striped align-middle">
              <thead>
                <tr>
                  <th>Business Name</th>
                  <th>Branch Name</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Country</th>
                  <th>Total Employees</th>
                  <th style={{ width: 120 }} className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      No blacklisted businesses found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r._id || r.id} className="table-danger">
                      <td>{r.businessName}</td>
                      <td>{r.branchName}</td>
                      <td>{r.city}</td>
                      <td>{r.state}</td>
                      <td>{r.country}</td>
                      <td>{r.totalEmployees}</td>
                      <td className="text-center">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => onView(r)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-end mt-3">
            <button className="btn btn-outline-secondary" onClick={onClose}>
              Back
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default function Manage() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!authAPI.isAuthenticated()) navigate("/");
  }, [navigate]);

  const [rows, setRows] = useState([]);
  const [blacklistedRows, setBlacklistedRows] = useState([]);

  const [loading, setLoading] = useState(true);
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [message, setMessage] = useState({ text: "", type: "info" });

  const [filterCountry, setFilterCountry] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCity, setFilterCity] = useState("");

  const [blacklistListOpen, setBlacklistListOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const showMessage = useCallback((text, type = "info") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "info" }), 3000);
  }, []);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterCountry) filters.country = filterCountry;
      if (filterState) filters.state = filterState;
      if (filterCity) filters.city = filterCity;

      const data = await getApprovedBusinesses(filters);
      setRows(data);
      if (data.length === 0) showMessage("No businesses found", "info");
    } catch (e) {
      showMessage(e.message || "Failed to load businesses", "error");
    } finally {
      setLoading(false);
    }
  }, [filterCountry, filterState, filterCity, showMessage]);

  const fetchBlacklisted = useCallback(async () => {
    setBlacklistLoading(true);
    try {
      const data = await getBlacklistedBusinesses();
      setBlacklistedRows(data);
    } catch (e) {
      showMessage(e.message || "Failed to load blacklisted businesses", "error");
    } finally {
      setBlacklistLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    document.title = "StarRec Manage Businesses";
    fetchBusinesses();
    fetchBlacklisted();
  }, [fetchBusinesses, fetchBlacklisted]);

  // filter dropdown lists
  const countries = useMemo(() => Array.from(new Set(rows.map((r) => r.country).filter(Boolean))).sort(), [rows]);

  const states = useMemo(() => {
    const list = rows
      .filter((r) => (filterCountry ? r.country === filterCountry : true))
      .map((r) => r.state)
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [rows, filterCountry]);

  const cities = useMemo(() => {
    const list = rows
      .filter((r) => (filterCountry ? r.country === filterCountry : true))
      .filter((r) => (filterState ? r.state === filterState : true))
      .map((r) => r.city)
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [rows, filterCountry, filterState]);

  const selected = useMemo(() => {
    const match = (x) => (x?._id || x?.id) === selectedId;
    return rows.find(match) || blacklistedRows.find(match) || null;
  }, [rows, blacklistedRows, selectedId]);

  const openView = useCallback((row) => {
    setSelectedId(row._id || row.id);
    setViewOpen(true);
  }, []);

  const closeView = useCallback(() => {
    setViewOpen(false);
    setSelectedId(null);
  }, []);

  const handleBlacklist = useCallback(
    async (businessId) => {
      const reason = prompt("Reason for blacklisting:", "Violation of terms");
      if (reason === null) return;
      if (!window.confirm("Blacklist this business?")) return;

      setProcessing(true);
      try {
        await blacklistBusiness(businessId, reason);
        await fetchBusinesses();
        await fetchBlacklisted();
        closeView();
        showMessage("Business blacklisted successfully!", "success");
      } catch (e) {
        showMessage(e.message || "Failed to blacklist business", "error");
      } finally {
        setProcessing(false);
      }
    },
    [fetchBusinesses, fetchBlacklisted, closeView, showMessage]
  );

  const handleRemoveBlacklist = useCallback(
    async (businessId) => {
      if (!window.confirm("Remove this business from blacklist?")) return;

      setProcessing(true);
      try {
        await removeFromBlacklist(businessId);
        await fetchBusinesses();
        await fetchBlacklisted();
        closeView();
        showMessage("Business removed from blacklist successfully!", "success");
      } catch (e) {
        showMessage(e.message || "Failed to unblacklist business", "error");
      } finally {
        setProcessing(false);
      }
    },
    [fetchBusinesses, fetchBlacklisted, closeView, showMessage]
  );

  return (
    <div className="container py-4">
      <h1 className="fs-4 fw-bold mb-3">Manage Companies</h1>

      <MessageBar message={message.text} type={message.type} onClose={() => setMessage({ text: "", type: "info" })} />

      {loading ? (
        <div className="text-center my-4">
          <div className="spinner-border" role="status" />
          <p className="mt-2">Loading businesses...</p>
        </div>
      ) : null}

      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Country</label>
              <select
                className="form-select"
                value={filterCountry}
                onChange={(e) => {
                  setFilterCountry(e.target.value);
                  setFilterState("");
                  setFilterCity("");
                }}
              >
                <option value="">All Countries</option>
                {countries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">State</label>
              <select
                className="form-select"
                value={filterState}
                onChange={(e) => {
                  setFilterState(e.target.value);
                  setFilterCity("");
                }}
                disabled={!filterCountry}
              >
                <option value="">All States</option>
                {states.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">City</label>
              <select
                className="form-select"
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
                disabled={!filterState}
              >
                <option value="">All Cities</option>
                {cities.map((ct) => (
                  <option key={ct} value={ct}>
                    {ct}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <div className="d-flex justify-content-between align-items-center gap-2">
                <button
                  className="btn btn-outline-danger w-100"
                  onClick={() => setBlacklistListOpen(true)}
                  disabled={blacklistLoading}
                >
                  {blacklistLoading ? "Loading..." : `Blacklisted (${blacklistedRows.length})`}
                </button>
                <button className="btn btn-outline-secondary" onClick={fetchBusinesses} disabled={loading} title="Refresh">
                  ↻
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead>
            <tr>
              <th>Business Name</th>
              <th>Branch Name</th>
              <th>City</th>
              <th>State</th>
              <th>Country</th>
              <th>Total Employees</th>
              <th>Status</th>
              <th style={{ width: 120 }} className="text-center">
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted">
                  No businesses found.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const isBlacklisted = r.blacklisted || r.isBlacklisted;
                return (
                  <tr key={r._id || r.id} className={isBlacklisted ? "table-danger" : ""}>
                    <td>{r.businessName}</td>
                    <td>{r.branchName}</td>
                    <td>{r.city}</td>
                    <td>{r.state}</td>
                    <td>{r.country}</td>
                    <td>{r.totalEmployees}</td>
                    <td>
                      {isBlacklisted ? (
                        <span className="badge bg-danger">Blacklisted</span>
                      ) : r.status === "approved" ? (
                        <span className="badge bg-success">Approved</span>
                      ) : (
                        <span className="badge bg-warning">{r.status}</span>
                      )}
                    </td>
                    <td className="text-center">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => openView(r)} disabled={processing}>
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <BlacklistedListModal
        open={blacklistListOpen}
        rows={blacklistedRows}
        onClose={() => setBlacklistListOpen(false)}
        onView={(row) => {
          setBlacklistListOpen(false);
          openView(row);
        }}
        loading={blacklistLoading}
      />

      <ViewBusinessModal
        open={viewOpen}
        business={selected}
        onClose={closeView}
        onBlacklist={handleBlacklist}
        onRemoveBlacklist={handleRemoveBlacklist}
        processing={processing}
      />
    </div>
  );
}
